/**
 * SHA-256, synchronous, in the browser.
 *
 * It exists for one reason: an SSH key fingerprint is `SHA256:<base64>` of the
 * key material, and that is the string a researcher can check against
 * `ssh-keygen -lf ~/.ssh/id_ed25519.pub` on the machine in question. Anything
 * shorter is a resemblance, not a fingerprint.
 *
 * `crypto.subtle.digest` is asynchronous, which would turn every row of a key
 * list into a pending state that renders twice; hashing 80 bytes is not worth
 * that, so the digest is computed here instead. FIPS 180-4, tested against the
 * standard vectors and against `ssh-keygen`'s own output.
 */

/** Round constants: the first 32 bits of the cube roots of the first 64 primes. */
const K = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1,
  0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
  0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
  0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147,
  0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
  0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b,
  0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
  0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
  0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
];

/** Initial hash: the first 32 bits of the square roots of the first 8 primes. */
const INITIAL = [
  0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c,
  0x1f83d9ab, 0x5be0cd19,
];

/**
 * Rotate a 32-bit word right.
 *
 * Args:
 *     word: The word to rotate.
 *     bits: How far, between 1 and 31.
 *
 * Returns:
 *     The rotated word, unsigned.
 */
function rotr(word: number, bits: number): number {
  return ((word >>> bits) | (word << (32 - bits))) >>> 0;
}

/**
 * Hash a byte string with SHA-256.
 *
 * Args:
 *     message: The bytes to hash.
 *
 * Returns:
 *     The 32-byte digest.
 */
export function sha256(message: Uint8Array): Uint8Array {
  // Pad to a multiple of 64 bytes: one 0x80 byte, zeroes, then the length in
  // bits as a big-endian 64-bit integer.
  const bitLength = message.length * 8;
  const padded = new Uint8Array((Math.floor((message.length + 8) / 64) + 1) * 64);
  padded.set(message);
  padded[message.length] = 0x80;
  const view = new DataView(padded.buffer);
  // A public key is a few hundred bytes; the high half of the length is zero,
  // and `setUint32` keeps this free of BigInt.
  view.setUint32(padded.length - 8, Math.floor(bitLength / 0x100000000));
  view.setUint32(padded.length - 4, bitLength >>> 0);

  const hash = [...INITIAL];
  const schedule = new Uint32Array(64);

  for (let chunk = 0; chunk < padded.length; chunk += 64) {
    for (let index = 0; index < 16; index += 1) {
      schedule[index] = view.getUint32(chunk + index * 4);
    }
    for (let index = 16; index < 64; index += 1) {
      const previous = schedule[index - 15] as number;
      const recent = schedule[index - 2] as number;
      const s0 = rotr(previous, 7) ^ rotr(previous, 18) ^ (previous >>> 3);
      const s1 = rotr(recent, 17) ^ rotr(recent, 19) ^ (recent >>> 10);
      schedule[index] =
        ((schedule[index - 16] as number) +
          s0 +
          (schedule[index - 7] as number) +
          s1) >>>
        0;
    }

    let [a, b, c, d, e, f, g, h] = hash as [
      number,
      number,
      number,
      number,
      number,
      number,
      number,
      number,
    ];

    for (let index = 0; index < 64; index += 1) {
      const s1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const choose = (e & f) ^ (~e & g);
      const temp1 =
        (h + s1 + choose + (K[index] as number) + (schedule[index] as number)) >>>
        0;
      const s0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const majority = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (s0 + majority) >>> 0;
      h = g;
      g = f;
      f = e;
      e = (d + temp1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) >>> 0;
    }

    const round = [a, b, c, d, e, f, g, h];
    for (let index = 0; index < 8; index += 1) {
      hash[index] = ((hash[index] as number) + (round[index] as number)) >>> 0;
    }
  }

  const digest = new Uint8Array(32);
  const out = new DataView(digest.buffer);
  hash.forEach((word, index) => out.setUint32(index * 4, word));
  return digest;
}
